import { AxiosResponse } from "axios";
import {
	ConfirmFXOrderAPIRequestType,
	ConfirmFXOrderAPIResponseType,
	CreateFXOrderAPIRequestType,
	CreateFXOrderAPIResponseType,
	GetRateAPIResponseType,
} from "../types/fx.type";
import {api, apiMockPostman} from "../utils/api.utils";

/**
 * Represents an interface for interacting with an FX API.
 *
 * @typedef {Object} FXAPIType
 * @property {function} getRate - A function that retrieves the exchange rate for a given set of currencies.
 * @property {function} createOrder - A function that creates a new FX order with the specified payload.
 * @property {function} confirmOrder - A function that confirms an existing FX order with the specified payload.
 * @property {function} cancelOrder - A function that cancels an existing FX order with the specified orderId.
 */
type FXAPIType = {
	getRate: (fxs: string[]) => Promise<AxiosResponse<GetRateAPIResponseType>>;
	createOrder: (
		payload: CreateFXOrderAPIRequestType
	) => Promise<AxiosResponse<CreateFXOrderAPIResponseType>>;
	confirmOrder: (
		payload: ConfirmFXOrderAPIRequestType
	) => Promise<AxiosResponse<ConfirmFXOrderAPIResponseType>>;
	cancelOrder: (orderId: string, reason:string) => Promise<AxiosResponse>;
};

/**
 * Represents a set of functions to interact with the FX API.
 *
 * @typedef {Object} FXAPIType
 *
 * @property {function(string[]): Promise<any>} getRate - Gets the exchange rate for the specified currencies.
 * @property {function(Object): Promise<any>} createOrder - Creates a new order with the specified payload.
 * @property {function(Object): Promise<any>} confirmOrder - Confirms an order with the specified payload.
 * @property {function(string): Promise<any>} cancelOrder - Cancels an order with the specified ID.
 */
const fxAPI: FXAPIType = {
	getRate: (fxs) => {
		return process.env.REACT_APP_USE_MOCK_API_GET_RATE === 'true'
			? apiMockPostman.get(`/rate${fxs.length > 0 ? `?fxs=${fxs.join(",")}` : ""}`)
			: api.get(`/rate${fxs.length > 0 ? `?fxs=${fxs.join(",")}` : ""}`);
	},
	createOrder: (payload) => {
		return process.env.REACT_APP_USE_MOCK_API_CREATE_ORDER === 'true'
			? apiMockPostman.post("/createOrder", payload)
			: api.post("/createOrder", payload);
	},
	confirmOrder: (payload) => {
		return process.env.REACT_APP_USE_MOCK_API_CONFIRM_ORDER === 'true'
			? apiMockPostman.post("/acceptQuote", payload)
			: api.post("/acceptQuote", payload);
	},
	cancelOrder: (orderId:string, reason:string) => {
		return process.env.REACT_APP_USE_MOCK_API_CANCEL_ORDER === 'true'
			? apiMockPostman.post("/rejectQuote", { orderId })
			: api.post("/rejectQuote", { orderId:orderId, reason:reason });
	},
};

export default fxAPI;
